// Pure helpers for rendering a stored unified diff as readable rows. Extracted
// from ProfileRevisionHistoryDialog so the logic can be unit-tested without MUI.

export type DiffToken = { t: 'same' | 'add' | 'rem'; s: string }
export type DiffRow =
  | { kind: 'changed'; tokens: DiffToken[] }
  | { kind: 'added'; text: string }
  | { kind: 'removed'; text: string }

// Split keeping whitespace/newlines as their own tokens so spacing is preserved.
export function tokenize(text: string): string[] {
  return text.split(/(\s+)/).filter((s) => s.length > 0)
}

// Word-level LCS diff → inline tokens (same / added / removed), so a one-word
// edit highlights just that word in context. No AI.
export function wordDiff(before: string, after: string): DiffToken[] {
  const a = tokenize(before)
  const b = tokenize(after)
  const m = a.length
  const n = b.length
  const dp: number[][] = Array.from({ length: m + 1 }, () =>
    new Array(n + 1).fill(0),
  )
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      dp[i][j] =
        a[i] === b[j]
          ? dp[i + 1][j + 1] + 1
          : Math.max(dp[i + 1][j], dp[i][j + 1])
    }
  }
  const out: DiffToken[] = []
  let i = 0
  let j = 0
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      out.push({ t: 'same', s: a[i] })
      i++
      j++
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ t: 'rem', s: a[i++] })
    } else {
      out.push({ t: 'add', s: b[j++] })
    }
  }
  while (i < m) out.push({ t: 'rem', s: a[i++] })
  while (j < n) out.push({ t: 'add', s: b[j++] })
  return out
}

// Turn the stored unified diff into only the changed rows — a modified line keeps
// its own words as context (inline word diff), and surrounding unchanged context
// lines are dropped so a small edit doesn't drag in whole unchanged paragraphs.
export function unifiedToRows(unified?: string): DiffRow[] {
  const rows: DiffRow[] = []
  let rem: string[] = []
  let add: string[] = []
  const flush = () => {
    // A single line lightly edited (most words unchanged) → inline word highlight,
    // keeping the line as its own context.
    if (rem.length === 1 && add.length === 1) {
      const tokens = wordDiff(rem[0], add[0])
      const same = tokens.filter((t) => t.t === 'same' && t.s.trim()).length
      const changed = tokens.filter((t) => t.t !== 'same' && t.s.trim()).length
      if (changed > 0 && same >= changed) {
        rows.push({ kind: 'changed', tokens })
        rem = []
        add = []
        return
      }
    }
    // Otherwise (a replaced line, or a multi-line rewrite) show the old text as a
    // red block then the new text as a green block — far easier to read than a
    // word-by-word interleave.
    for (const r of rem) if (r.trim()) rows.push({ kind: 'removed', text: r })
    for (const a of add) if (a.trim()) rows.push({ kind: 'added', text: a })
    rem = []
    add = []
  }
  for (const line of (unified || '').split('\n')) {
    if (
      line.startsWith('+++') ||
      line.startsWith('---') ||
      line.startsWith('@@')
    ) {
      continue
    }
    if (line.startsWith('+')) add.push(line.slice(1))
    else if (line.startsWith('-')) rem.push(line.slice(1))
    else flush() // unchanged context line → boundary; not rendered
  }
  flush()
  return rows
}

// Which "what changed" view a revision should show: the plain-language bullets
// (auto-regens), the diff (user edits), a "no changes" notice (a no-op regen with a
// previous version), or nothing (the original, which has no previous version).
export function revisionWhatChangedKind(detail: {
  changes_summary?: string[] | null
  diff?: { unified?: string; from_version?: number | null } | null
}): 'summary' | 'diff' | 'nochange' | 'none' {
  if (detail.changes_summary && detail.changes_summary.length > 0) return 'summary'
  if (unifiedToRows(detail.diff?.unified).length > 0) return 'diff'
  return detail.diff?.from_version ? 'nochange' : 'none'
}
